import { action, computed, makeObservable, observable } from 'mobx';

export const initialCursor = {};

export interface PaginationParams {
  limit?: number;
}

export class Pagination {
  limit: number;
  page = 1;
  cursor: string | typeof initialCursor | null = initialCursor;

  constructor(params: PaginationParams) {
    this.limit = params.limit ?? 100;

    makeObservable(this, {
      limit: observable.ref,
      page: observable.ref,
      cursor: observable.ref,
      offset: computed,
      changeLimit: action,
      nextPage: action,
      prevPage: action,
      moveToPage: action,
    });
  }

  changeOffset(offset: number) {
    this.page = Math.floor(offset / this.limit) + 1;
  }

  effectiveLimit(maxLimit = Infinity) {
    const { limit } = this;
    return limit > 0 ? limit : maxLimit;
  }

  eagerLimitQuery() {
    const limit = this.effectiveLimit();
    return limit === Infinity ? {} : this.limitQuery();
  }

  limitQuery(maxLimit?: number) {
    const { page } = this;
    const limit = this.effectiveLimit(maxLimit);
    return {
      limit,
      offset: limit * (page - 1),
    };
  }

  public toQueryString(): Record<string, string> {
    return {
      page: String(this.page),
      limit: String(this.limit),
    };
  }

  changeLimit(limit: number) {
    this.limit = limit;
  }

  nextPage() {
    this.page += 1;
  }

  prevPage() {
    this.page -= 1;
  }

  moveToPage(page = 0) {
    this.page = page;
  }

  get offset() {
    const { limit, page } = this;
    return limit * (page - 1);
  }

  pageBounds(page: number) {
    const { limit } = this;
    const from = limit * (page - 1);
    return {
      from,
      to: from + limit,
    };
  }

  pageOf(index: number) {
    return Math.floor(index / this.limit) + 1;
  }
}
